import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import {
  AuthMethod,
  UserAuthError,
  UserAuthResponseData,
} from 'src/app/@data-services/user-auth/user-auth.data-model';
import { UserAuthDataService } from 'src/app/@data-services/user-auth/user-auth.data-service';
import { MixpanelService } from 'src/app/@services/mixpanel.service';
import { AuthStore } from 'src/app/@core/auth/stores/auth-store';
import { AuthStateService } from 'src/app/@store/auth-state.service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';
import { ExternalRegistrationStateService } from 'src/app/@store/external-registration-state.service';
import {
  ENROLL_EVENT_REPORT,
  ENROLLED_USER_SESSION,
} from 'src/app/@constants/local-storage-key-constants';
import { removeLocalStorageItem } from 'src/app/@utils/local-storage-util';

export interface AuthResult {
  success: boolean;
  error?: UserAuthError;
}

@Injectable({
  providedIn: 'root',
})
export class UserAuthService {
  private readonly _authDataService = inject(UserAuthDataService);
  private readonly _authStateService = inject(AuthStateService);
  private readonly _authStore = inject(AuthStore);
  private readonly _eventConfigStateService = inject(EventConfigStateService);
  private readonly _mixpanelService = inject(MixpanelService);
  private readonly _externalRegistrationStateService = inject(
    ExternalRegistrationStateService
  );

  authenticate(
    eventNameIdentifier: string,
    authMethod: AuthMethod,
    identifier: string
  ): Observable<AuthResult> {
    this._authStateService.setEventNameIdentifier(eventNameIdentifier);
    this._authStateService.setStatus('authenticating');

    return this._authDataService
      .validateUser(eventNameIdentifier, authMethod, identifier)
      .pipe(
        tap((response: UserAuthResponseData) => {
          this._handleAuthResponse(response);
        }),
        map((response: UserAuthResponseData) => ({
          success: response.success && response.resolved,
          error: response.error,
        })),
        catchError(() => {
          const networkError: UserAuthError = {
            code: 'NETWORK_ERROR',
            message:
              'Unable to connect to authentication service. Please try again.',
          };
          this._authStateService.setError(networkError);
          return of({ success: false, error: networkError });
        })
      );
  }

  authenticateWithEmail(
    eventNameIdentifier: string,
    email: string
  ): Observable<AuthResult> {
    return this.authenticate(eventNameIdentifier, 'email', email);
  }

  authenticateWithToken(
    eventNameIdentifier: string,
    token: string
  ): Observable<AuthResult> {
    return this.authenticate(eventNameIdentifier, 'token', token);
  }

  authenticateWithRegistrationId(
    eventNameIdentifier: string,
    registrationId: string
  ): Observable<AuthResult> {
    return this.authenticate(
      eventNameIdentifier,
      'registrationId',
      registrationId
    );
  }

  isAuthenticationRequired(): boolean {
    const features = this._eventConfigStateService.$featuresConfig();
    return features.RequireAuthentication ?? false;
  }

  isEmailAuthAllowed(): boolean {
    const features = this._eventConfigStateService.$featuresConfig();
    return features.AllowEmailAuth ?? true;
  }

  isTokenAuthAllowed(): boolean {
    const features = this._eventConfigStateService.$featuresConfig();
    return features.AllowTokenAuth ?? true;
  }

  checkExistingAuth(eventNameIdentifier: string): boolean {
    return this._authStateService.isValidForEvent(eventNameIdentifier);
  }

  logout(): void {
    this._authStateService.reset();
    this._mixpanelService.reset();
    this._externalRegistrationStateService.reset();
    removeLocalStorageItem(ENROLL_EVENT_REPORT);
    removeLocalStorageItem(ENROLLED_USER_SESSION);
  }

  private _handleAuthResponse(response: UserAuthResponseData): void {
    if (response.success && response.resolved && response.data) {
      // Pass Cognito token expiry for session sync
      const tokenExpiry = this._authStore.$sessionExpiry() ?? undefined;
      this._authStateService.setUser(response.data, tokenExpiry);

      // Sync with MixpanelService for tracking
      if (response.data.email) {
        this._mixpanelService.setUserInfo(response.data.email);
      }
      if (response.data.registrationId) {
        this._externalRegistrationStateService.setRegistrationId(
          response.data.registrationId
        );
      }

      return;
    }

    if (response.error) {
      const status = this._mapErrorCodeToStatus(response.error.code);
      this._authStateService.setError(response.error, status);
      return;
    }

    // Fallback for unexpected response format
    const unknownError: UserAuthError = {
      code: 'USER_NOT_FOUND',
      message: 'Unable to verify your identity. Please try again.',
    };
    this._authStateService.setError(unknownError);
  }

  private _mapErrorCodeToStatus(
    code: string
  ): 'unresolved' | 'blocked' | 'no_entitlement' | 'error' {
    switch (code) {
      case 'BLOCKED_USER':
        return 'blocked';
      case 'NO_ENTITLEMENT':
      case 'BADGE_NOT_ALLOWED': // KnowledgeHub: badge type not allowed
        return 'no_entitlement';
      case 'USER_NOT_FOUND':
      case 'MULTIPLE_MATCHES':
        return 'unresolved';
      default:
        return 'error';
    }
  }
}
